import * as React from 'react';
import { showGlobalModal } from 'core/components/GlobalModal';
import { blogApi } from '../api/blogApi';
import { getApiErrorMessage } from '../utils/apiResponse';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 20;

const getTaskId = (response: any) => {
  const data = response?.data?.data;
  if (typeof data === 'string' || typeof data === 'number') return String(data);
  return String(data?.taskId || data?.task_id || '');
};

export const useImageEnhancer = () => {
  const [isEnhancingImage, setIsEnhancingImage] = React.useState(false);

  const enhanceImage = React.useCallback(async (imageUrl: string) => {
    if (!imageUrl || imageUrl.startsWith('blob:')) return undefined;

    setIsEnhancingImage(true);
    try {
      const response = await blogApi.enhanceImage({
        user_image_url: imageUrl,
        face_upsample: true,
        background_enhance: true,
        upscale: 2,
        fidelity_weight: 0.5,
      });
      const taskId = getTaskId(response);
      if (!taskId) throw new Error('Unable to start image enhancement');

      for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
        await new Promise(resolve => window.setTimeout(resolve, POLL_INTERVAL_MS));
        const result = await blogApi.getGeneratedImage(taskId);
        const enhancedUrl = result?.data?.data;
        if (
          typeof enhancedUrl === 'string' &&
          enhancedUrl.trim() &&
          enhancedUrl !== 'pending'
        ) {
          return enhancedUrl;
        }
      }

      throw new Error('Image enhancement timed out. Please try again.');
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Failed to enhance image. Please try again.'),
        'error'
      );
      return undefined;
    } finally {
      setIsEnhancingImage(false);
    }
  }, []);

  return { enhanceImage, isEnhancingImage };
};
